/**
 * Global keyboard shortcut registry per spec section 7.7.
 *
 * The shell mounts `useKeyboardShortcuts` once at the root so the
 * bindings survive route changes. Each shortcut is a simple
 * { key, handler, description } record; the cheat sheet dialog
 * reads from the same registry to stay in sync with the actual
 * bindings.
 *
 * Cmd-K is wired in its own hook (`use-command-bar-shortcut.ts`)
 * because the command bar is opt-in across the application. This
 * module handles the secondary shortcuts: `?` for the cheat sheet,
 * navigation letters (T/S/F/W/D/X/P), and the Esc-to-close pattern
 * (which Radix already handles inside dialogs).
 */

import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useNavigate } from '@tanstack/react-router';

interface CheatSheetState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useCheatSheet = create<CheatSheetState>((set) => ({
  isOpen: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

export interface ShortcutEntry {
  /** The keystroke the cheat sheet displays. */
  key: string;
  /** What the binding does. */
  description: string;
}

/**
 * The canonical list of bindings the cheat sheet renders. Each entry
 * matches an actual keydown listener in the shell or in this module;
 * keep them in sync when adding a new binding.
 */
export const SHORTCUTS: ShortcutEntry[] = [
  { key: '⌘K / Ctrl-K', description: 'Open the command bar' },
  { key: '?', description: 'Open this cheat sheet' },
  { key: 'G then T', description: 'Go to Tasks' },
  { key: 'G then S', description: 'Go to Sales' },
  { key: 'G then H', description: 'Go to Home' },
  { key: 'Esc', description: 'Close any open dialog' },
];

/**
 * Window-level handler for `?` and the G-prefixed navigation
 * keystrokes. Listens for "g" followed within 1.2 seconds by a
 * letter that names a destination. Other keystrokes reset the
 * pending prefix so a stray "g" does not lock the user out.
 */
export function useKeyboardShortcuts(): void {
  const toggleCheatSheet = useCheatSheet((s) => s.toggle);
  const navigate = useNavigate();

  // navigate is captured in a ref so the keydown listener can read the
  // latest navigate function without re-binding on every render. SPA
  // routing keeps Clerk's session alive — calling window.location.assign
  // (the prior implementation) triggered a full reload that re-mounted
  // the Clerk gate and flashed "SIGNING YOU IN…" between every G-letter
  // navigation (UAT 1.7).
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    let prefix: 'g' | null = null;
    let prefixTimer: ReturnType<typeof setTimeout> | null = null;

    function clearPrefix() {
      prefix = null;
      if (prefixTimer) clearTimeout(prefixTimer);
      prefixTimer = null;
    }

    function onKeyDown(event: KeyboardEvent) {
      // Don't fire while the user is typing in an input or textarea.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      if (event.key === '?' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        toggleCheatSheet();
        return;
      }

      if (prefix === 'g') {
        const dest = navigationDestinationFor(event.key.toLowerCase());
        if (dest) {
          event.preventDefault();
          // SPA navigation keeps the React root mounted so Clerk does
          // not re-bootstrap on every G-letter combo.
          navigateRef.current({ to: dest });
        }
        clearPrefix();
        return;
      }

      if (event.key.toLowerCase() === 'g' && !event.metaKey && !event.ctrlKey) {
        prefix = 'g';
        prefixTimer = setTimeout(clearPrefix, 1200);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearPrefix();
    };
  }, [toggleCheatSheet]);
}

/**
 * The narrow set of routes the G-prefix can target. Literal-typed so
 * TanStack Router's typed `to` accepts the value without a cast.
 */
type GNavDestination = '/' | '/tasks' | '/sales';

function navigationDestinationFor(letter: string): GNavDestination | null {
  switch (letter) {
    case 'h':
      return '/';
    case 't':
      return '/tasks';
    case 's':
      return '/sales';
    default:
      return null;
  }
}
